import { Injectable, NgZone } from '@angular/core';
import { EditorColorTarget } from '../_models';
import { ColorSwatchPickerComponent } from '../_shared';

// Drives the color swatch picker for TinyMCE color buttons; the screen registers it via appEditorColorPicker.
// Keeps the editor selection while the popover has focus, applies colors live without undo levels,
// then records one undo step on close (or reverts on cancel).
@Injectable()
export class EditorColorService {
  private picker: ColorSwatchPickerComponent = null;
  private editor: any = null;
  private key = '';
  private target: EditorColorTarget = null;
  private range: Range = null;
  private snapshot = '';
  private changed = false;

  constructor(private zone: NgZone) {}

  register(picker: ColorSwatchPickerComponent) {
    this.picker = picker;
  }

  // The screen holding the picker is gone: drop the editor session along with it.
  unregister(picker: ColorSwatchPickerComponent) {
    if (this.picker === picker) {
      this.editor = null;
      this.picker = null;
    }
  }

  // Toolbar buttons run outside Angular; clicking the same button (key) again closes the popover.
  // No-op on screens without a registered picker.
  toggle(editor: any, key: string, anchor: HTMLElement, target: EditorColorTarget) {
    this.zone.run(() => {
      if (this.picker) {
        const isSameButton = this.picker.isOpen && this.editor === editor && this.key === key;
        if (this.picker.isOpen) {
          this.picker.dismiss();
        }
        if (!isSameButton) {
          this.editor = editor;
          this.key = key;
          this.target = target;
          this.range = editor.selection.getRng().cloneRange();
          this.snapshot = editor.getContent();
          this.changed = false;
          this.picker.color = target.color;
          this.picker.allowRemove = !!target.remove;
          this.picker.openAt(anchor);
        }
      }
    });
  }

  apply(color: string) {
    if (this.editor) {
      this.editor.selection.setRng(this.range);
      this.editor.undoManager.ignore(() => this.target.apply(color));
      this.range = this.editor.selection.getRng().cloneRange();
      this.editor.fire('change');
      this.changed = true;
    }
  }

  remove() {
    if (this.editor && this.target.remove) {
      this.editor.selection.setRng(this.range);
      this.target.remove();
      this.editor = null;
    }
  }

  close(keepColor: boolean) {
    if (this.editor) {
      if (keepColor) {
        this.editor.undoManager.add();
      } else if (this.changed) {
        this.editor.setContent(this.snapshot);
        if (this.target.revert) {
          this.target.revert();
        }
        this.editor.fire('change');
      }
      this.editor = null;
    }
  }

  // Clicks inside any editor iframe never reach the picker's document listener, so editors close it themselves.
  dismiss() {
    if (this.picker && this.picker.isOpen) {
      this.zone.run(() => this.picker.dismiss());
    }
  }

  // The editing editor is being destroyed (e.g. navigation): forget it, then close the popover.
  release(editor: any) {
    if (this.editor === editor) {
      this.editor = null;
      this.dismiss();
    }
  }
}
